import type { QueryClient } from '@tanstack/react-query';
import { type NavigateOptions, redirect } from '@tanstack/react-router';

import type { AppRouterContext } from 'providers/AppRouter';
import {
  isVerified,
  otpChallengeQueryOptions,
} from 'services/partnersSelfSignup/otp';
import { rumService } from 'services/rum';

/**
 * Where the Partner goes once their email is verified. The route that hosts
 * OTP supplies it, because that is Lead or Opportunity logic and OTP holds
 * none.
 *
 * The loop rule has two parts (ADR 024):
 * - OTP calls it only for a verified email, and a throw never moves the
 *   Partner on: on enter the page renders the iframe, and after `OK` it runs
 *   the failure path.
 * - The host's implementation does the host's work and throws when that work
 *   fails, including when the server refuses the hash. OTP moves on by itself
 *   on enter, so one that resolves without asking the server, in front of a
 *   destination that sends the Partner back to OTP, would loop.
 */
export type OnVerified = () => Promise<NavigateOptions>;

/** What an OTP route puts in its context, for its loader and its page. */
export type OtpRouteContext = AppRouterContext & {
  email: string;
  onVerified: OnVerified;
};

type RedirectIfVerifiedProps = {
  queryClient: QueryClient;
  email: string;
  onVerified: OnVerified;
  cause: 'preload' | 'enter' | 'stay';
  // Signup and the Opportunity share this function, so the log names the route.
  route: { id: string };
};

/**
 * Runs in the `beforeLoad` of an OTP route when the Partner enters the page. If the email is already verified, it moves the Partner on before the page renders, so no iframe shows and no email is sent. Otherwise it does nothing.
 *
 * It is `beforeLoad` and not the loader because `beforeLoad` runs before the loader on every entry. A stale match renders at once while its loader reloads in the background, so a redirect from the loader lands after the page has rendered (ADR 024).
 * https://tanstack.com/router/latest/docs/guide/data-loading#route-loading-lifecycle
 *
 * Hovering a link (`preload`) and reloading the page in place (`stay`, such as a language change) do nothing: `onVerified` does the host's work, and a hover or a reload is not a Partner moving on.
 */
export async function redirectIfVerified({
  queryClient,
  email,
  onVerified,
  cause,
  route,
}: RedirectIfVerifiedProps): Promise<void> {
  if (cause !== 'enter') {
    return;
  }

  if (!(await isVerified({ queryClient, email }))) {
    return;
  }

  const navigateOptions = await onVerified().catch(error => {
    // The page renders as it would for any other entry, and the iframe shows
    // its "already validated" page. Its "Try again" runs the page's failure
    // path.
    rumService.addError(
      new Error('[redirectIfVerified] onVerified failed on enter'),
      { error, routeId: route.id },
    );
    return undefined;
  });

  if (navigateOptions) {
    // https://tanstack.com/router/latest/docs/api/router/redirectFunction
    throw redirect(navigateOptions);
  }
}

type EmailValidationLoaderProps = {
  context: OtpRouteContext;
  cause: 'preload' | 'enter' | 'stay';
};

/**
 * Runs when the Partner enters the OTP page, after `redirectIfVerified` has moved on a verified email. Warms the Email-validation challenge, which the page then reads from the query. It reuses the cached challenge. Otherwise it resumes the pending challenge of this browser, and creates one only when none is pending.
 *
 * Hovering a link (`preload`) and reloading the page in place (`stay`, such as a language change) do nothing: a create sends an email.
 * https://tanstack.com/router/latest/docs/guide/data-loading#loader-parameters
 */
export async function emailValidationLoader({
  context,
  cause,
}: EmailValidationLoaderProps): Promise<void> {
  if (cause !== 'enter') {
    return;
  }

  const { queryClient, email } = context;

  // The page reads the error from the query.
  await queryClient
    .query(otpChallengeQueryOptions({ email }))
    .catch(() => undefined);
}
